/**
 * DashboardScreen — inicio del editor AetherLite.
 *
 * KPIs (planes totales, clientes activos, último plan) + lista de planes
 * recientes + CTA gigante para crear plan nuevo. Si el user está cerca de
 * su límite de clientes, muestra un banner soft de upgrade.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../lib/toast";
import { Icon } from "../lib/Icon";
import { EmptyState } from "../lib/components";
import { getUsage, type UsageSummary } from "../api/auth";
import { createShare, deletePlan, listPlans, publicShareUrl, type PlanListItem } from "../api/plans";
import { getClient, type ClientFull } from "../api/clients";
import type { Route } from "../types";
import { DuplicatePlanModal } from "./plan/DuplicatePlanModal";
import { CreatePlanModal } from "./plan/CreatePlanModal";

interface DashboardScreenProps {
  go: (r: Route, query?: string) => void;
}

/** Lee `client_id` del hash actual (#/dashboard?client_id=42). */
function parseClientIdFromHash(): number | null {
  const raw = (typeof window !== "undefined" ? window.location.hash : "").replace(/^#\/?/, "");
  const qsIdx = raw.indexOf("?");
  if (qsIdx < 0) return null;
  const params = new URLSearchParams(raw.slice(qsIdx + 1));
  const cid = params.get("client_id");
  return cid ? Number(cid) : null;
}

function relativeTime(iso?: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return "hace segundos";
  const min = Math.round(sec / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  if (d < 7) return `hace ${d} d`;
  const w = Math.round(d / 7);
  if (w < 5) return `hace ${w} sem`;
  const months = Math.round(d / 30);
  if (months < 12) return `hace ${months} m`;
  return `hace ${Math.round(d / 365)} a`;
}

export function DashboardScreen({ go }: DashboardScreenProps) {
  const { user } = useAuth();
  const toast = useToast();
  const [plans, setPlans] = useState<PlanListItem[] | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // Filtro por cliente (cuando se navega desde ClientsScreen → "Ver planes")
  const [clientFilter, setClientFilter] = useState<number | null>(parseClientIdFromHash());
  const [clientFilterName, setClientFilterName] = useState<string>("");
  /** Cliente completo del filtro — sirve como `lockedClient` para el modal
   *  de creación cuando el coach está filtrando por un cliente específico. */
  const [clientFilterFull, setClientFilterFull] = useState<ClientFull | null>(null);
  /** Modal de creación abierto / cerrado. */
  const [createPlanOpen, setCreatePlanOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [p, u] = await Promise.all([listPlans(), getUsage()]);
      setPlans(p);
      setUsage(u);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Sincronizar el filtro con el hash (back/forward del browser, navegación interna)
  useEffect(() => {
    const onHash = () => setClientFilter(parseClientIdFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Resolver el nombre del cliente filtrado (para mostrarlo en el banner)
  useEffect(() => {
    if (!clientFilter) {
      setClientFilterName("");
      setClientFilterFull(null);
      return;
    }
    let cancelled = false;
    getClient(clientFilter)
      .then((c) => {
        if (!cancelled) {
          setClientFilterName(c.full_name);
          setClientFilterFull(c);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setClientFilterName("");
          setClientFilterFull(null);
        }
      });
    return () => { cancelled = true; };
  }, [clientFilter]);

  /** Planes filtrados por cliente si hay filtro activo. */
  const filteredPlans = useMemo(() => {
    if (!plans) return [];
    if (!clientFilter) return plans;
    return plans.filter((p) => p.client_id === clientFilter);
  }, [plans, clientFilter]);

  const clearClientFilter = useCallback(() => {
    setClientFilter(null);
    window.location.hash = "#/dashboard";
  }, []);

  const recent = useMemo(() => {
    const list = filteredPlans.slice();
    list.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    // Sin filtro: top 5 recientes. Con filtro: TODOS los del cliente.
    return clientFilter ? list : list.slice(0, 5);
  }, [filteredPlans, clientFilter]);

  const lastEdited = recent[0];

  const nearLimit = useMemo(() => {
    if (!usage) return false;
    if (usage.is_unlimited || usage.clients_limit < 0) return false;
    return usage.clients_active >= Math.floor(usage.clients_limit * 0.8);
  }, [usage]);

  const firstName = (user?.name || "").split(" ")[0] || "Coach";

  const onShare = useCallback(
    async (planId: number) => {
      try {
        const share = await createShare(planId);
        const url = publicShareUrl(share.short_code);
        try {
          await navigator.clipboard.writeText(url);
          toast("Link copiado al portapapeles");
        } catch {
          toast("Link creado: " + url);
        }
      } catch (e) {
        toast("No pude crear el link de compartir");
        console.error(e);
      }
    },
    [toast],
  );

  /** ID del plan que se está eliminando — deshabilita sus acciones mientras tanto. */
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const onDelete = useCallback(
    async (plan: PlanListItem) => {
      if (deletingId) return;
      if (
        !confirm(
          `¿Eliminar el plan "${plan.name || "Sin título"}"? Esta acción no se puede deshacer.`,
        )
      ) {
        return;
      }
      setDeletingId(plan.id);
      // Optimista: lo sacamos de la lista ya. Si falla, recargamos para revertir.
      setPlans((prev) => (prev ? prev.filter((p) => p.id !== plan.id) : prev));
      try {
        await deletePlan(plan.id);
        toast("Plan eliminado");
      } catch (e) {
        toast("No pude eliminar el plan: " + (e instanceof Error ? e.message : ""));
        load();
      } finally {
        setDeletingId(null);
      }
    },
    [deletingId, toast, load],
  );

  /** Plan que se está duplicando (abre el modal de selección de destino).
   *  null = no hay modal abierto. */
  const [duplicating, setDuplicating] = useState<PlanListItem | null>(null);

  /** Tras duplicar, ofrecemos abrir el nuevo plan directamente. */
  const onDuplicated = useCallback(
    (newPlanId: number, destClient: ClientFull) => {
      void destClient;
      // Refrescamos la lista para que aparezca el plan nuevo arriba.
      load();
      // Navegar al editor del nuevo plan directamente — flujo típico: el coach
      // duplica y entra a ajustar lo específico (ej. cambiar un ejercicio).
      go("plan-builder", `id=${newPlanId}`);
    },
    [load, go],
  );

  if (loading && !plans) {
    return (
      <div className="page" style={{ paddingTop: 24 }}>
        <div className="muted">Cargando…</div>
      </div>
    );
  }

  if (err && !plans) {
    return (
      <div className="page" style={{ paddingTop: 48 }}>
        <EmptyState
          icon="sparkle"
          title="Algo salió mal"
          body={err}
          action={
            <button className="btn btn-primary" onClick={load}>
              Reintentar
            </button>
          }
        />
      </div>
    );
  }

  const totalPlans = plans?.length || 0;
  const isEmpty = clientFilter ? filteredPlans.length === 0 : totalPlans === 0;

  return (
    <div className="page al-dash">
      {/* ---------- Hero / saludo ---------- */}
      <div className="al-dash-hero">
        <div>
          <h1 className="al-dash-greet">Hola, {firstName}</h1>
          {user?.tagline ? (
            <p className="al-dash-tagline">{user.tagline}</p>
          ) : (
            <p className="al-dash-tagline muted">Tu cabina de entrenamiento</p>
          )}
        </div>
        <button
          className="btn btn-primary btn-lg al-dash-cta-hero"
          onClick={() => setCreatePlanOpen(true)}
        >
          <Icon name="plus" size={16} />
          Crear nuevo plan
        </button>
      </div>

      {/* ---------- Banner de filtro por cliente ---------- */}
      {clientFilter && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "10px 16px",
            background: "rgba(124, 58, 237, 0.08)",
            border: "1px solid var(--primary)",
            borderRadius: 10,
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <Icon name="user" size={14} />{" "}
            <b>Planes de {clientFilterName || "este cliente"}</b>{" "}
            <span className="muted tiny">
              · {filteredPlans.length} {filteredPlans.length === 1 ? "plan" : "planes"}
            </span>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={clearClientFilter}>
            <Icon name="x" size={12} /> Ver todos los planes
          </button>
        </div>
      )}

      {/* ---------- Upgrade soft banner ---------- */}
      {nearLimit && usage && (
        <div className="al-dash-banner">
          <div>
            <b>Estás cerca de tu límite.</b>{" "}
            <span className="muted">
              Vas {usage.clients_active}/{usage.clients_limit} clientes activos en tu {usage.plan_label}.
            </span>
          </div>
          <a className="btn btn-secondary btn-sm" href="mailto:nilton.alfonso@nilabs.co?subject=Upgrade%20AetherLite">
            Ver opciones
          </a>
        </div>
      )}

      {/* ---------- KPIs ---------- */}
      <div className="al-kpi-grid">
        <div className="card al-kpi">
          <div className="stat-label">Planes totales</div>
          <div className="stat-value">{totalPlans}</div>
          <div className="tiny muted">{totalPlans === 1 ? "1 plan creado" : `${totalPlans} planes creados`}</div>
        </div>

        <div className="card al-kpi">
          <div className="stat-label">Clientes activos</div>
          <div className="stat-value">
            {usage?.clients_active ?? 0}
            <span className="al-kpi-limit">
              {" "}
              / {usage && (usage.is_unlimited || usage.clients_limit < 0) ? "∞" : usage?.clients_limit ?? 0}
            </span>
          </div>
          <div className="tiny muted">{usage?.plan_label || "Plan"}</div>
        </div>

        <div className="card al-kpi">
          <div className="stat-label">Último plan editado</div>
          <div className="al-kpi-last">
            {lastEdited ? (
              <>
                <div className="al-kpi-last-title" title={lastEdited.name}>
                  {lastEdited.name || "Sin título"}
                </div>
                <div className="tiny muted">{relativeTime(lastEdited.updated_at)}</div>
              </>
            ) : (
              <div className="tiny muted">Aún no has editado planes</div>
            )}
          </div>
        </div>
      </div>

      {/* ---------- Recientes / Empty ---------- */}
      {isEmpty ? (
        <div className="card" style={{ padding: 32 }}>
          <EmptyState
            icon="sparkle"
            title={clientFilter ? `${clientFilterName || "Este cliente"} aún no tiene planes` : "Crea tu primer plan"}
            body={
              clientFilter
                ? "Creá el primer plan para este cliente — sus datos se precargan automáticamente."
                : "Comienza con una plantilla o desde cero. Tu cliente recibirá un link mobile-friendly con su rutina, dieta, cardio y más."
            }
            action={
              <button
                className="btn btn-primary"
                onClick={() => setCreatePlanOpen(true)}
              >
                <Icon name="plus" size={14} /> {clientFilter ? "Crear plan para el cliente" : "Empezar"}
              </button>
            }
          />
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              {clientFilter ? `Planes de ${clientFilterName || "el cliente"}` : "Planes recientes"}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => go("clients")}>
              Ver por cliente <Icon name="arrow" size={14} />
            </button>
          </div>

          <div className="al-dash-list">
            {recent.map((p) => (
              <div className="al-dash-plan" key={p.id}>
                <div className="al-dash-plan-info">
                  <div className="al-dash-plan-title">{p.name || "Plan sin título"}</div>
                  <div className="tiny muted">
                    {p.client_name ? <>· {p.client_name} · </> : null}
                    Editado {relativeTime(p.updated_at)}
                  </div>
                </div>
                <div className="al-dash-plan-actions">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setDuplicating(p)}
                    title="Duplicar este plan para otro cliente (mantiene rutina/cardio/suplementos; recalcula macros)"
                  >
                    <Icon name="grid" size={14} /> Duplicar
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => onShare(p.id)}
                    title="Crear link público"
                  >
                    <Icon name="send" size={14} /> Compartir
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => go("plan-builder", `id=${p.id}`)}
                  >
                    Editar
                  </button>
                  <button
                    className="btn btn-ghost btn-sm al-dash-plan-del"
                    onClick={() => onDelete(p)}
                    disabled={deletingId === p.id}
                    title="Eliminar plan"
                    aria-label={`Eliminar plan ${p.name || "sin título"}`}
                  >
                    <Icon name="x" size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {duplicating && (
        <DuplicatePlanModal
          source={duplicating}
          onClose={() => setDuplicating(null)}
          onDuplicated={onDuplicated}
        />
      )}

      {createPlanOpen && (
        <CreatePlanModal
          // Si el coach está filtrando por un cliente, ese cliente queda fijo
          // (no le hacemos elegir de nuevo). Sin filtro, parte vacío.
          lockedClient={clientFilterFull}
          onClose={() => setCreatePlanOpen(false)}
          onCreated={(planId) => {
            setCreatePlanOpen(false);
            load();
            go("plan-builder", `id=${planId}`);
          }}
        />
      )}
    </div>
  );
}
